import { Archive, Print } from "@mui/icons-material";
import {
    Button,
    CircularProgress,
    Drawer,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../AppContext";
import { useLoadDoctype } from "../../shared/hooks/useFrappeDoctype";
import { useClosedInvoices, useCancelInvoice } from "../../shared/hooks/useInvoicesData";
import { useGetDocTypeList } from "../../shared/hooks/useFrappeDoctype";
import {
    useGetAllTables,
    usePrintOrder,
} from "../../shared/hooks/useTablesData";
import { PosContext } from "../../shared/PosContext";
import { dateTimeFormat } from "../../utils/dateUtils";
import {
    getOrderShortName,
    getTakeawayUrlSegment,
} from "../../utils/floorUtils";
import {
    buildDownloadPdfUrl,
    getProfileLetterhead,
} from "../../utils/printFormatUrl";
import SimpleWidgetsHeader from "../SimpleWidgetsHeader/SimpleWidgetsHeader";
import Spinner from "../Spinner";
import { useStyles } from "./styles";
import ClosedOrderItemRow from "./ClosedOrderItemRow";
import { useTranslation } from "react-i18next";
import ConfirmationDialog from "../ConfirmationDialog";
import { useNavigate } from "react-router-dom";

const getVisibleCustomerName = (customerName, customer) => {
    const rawValue = String(customerName || customer || "").trim();
    if (!rawValue) return "";

    const normalized = rawValue.toLowerCase();
    const blockedWords = ["table", "takeaway", "default"];

    return blockedWords.some((word) => normalized.includes(word))
        ? ""
        : rawValue;
};

const orderTypeStyles = {
    dine_in: {
        color: "#2e7d32",
        backgroundColor: "rgba(46, 125, 50, 0.12)",
        borderColor: "rgba(46, 125, 50, 0.3)",
    },
    delivery: {
        color: "#1565c0",
        backgroundColor: "rgba(21, 101, 192, 0.12)",
        borderColor: "rgba(21, 101, 192, 0.3)",
    },
    takeaway: {
        color: "#ed6c02",
        backgroundColor: "rgba(237, 108, 2, 0.12)",
        borderColor: "rgba(237, 108, 2, 0.3)",
    },
};

const orderTypeBadgeStyle = (type) => ({
    ...(orderTypeStyles[type] || orderTypeStyles.takeaway),
    border: "1px solid",
    borderRadius: 999,
    display: "inline-block",
    fontWeight: 700,
    lineHeight: 1.4,
    maxWidth: "100%",
    overflow: "hidden",
    padding: "0.1rem 0.5rem",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

function ClosedOrders({ open, onClose }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        posProfile,
        elevatedPrivileges,
        restaurantSettings,
        isRetailPos,
        isTabsPos,
    } = useContext(AppContext);
    const invoicePrinter = posProfile?.invoice_printer;
    const invoicePrintFormat = posProfile?.invoice_print_format || "POS";
    const selectedLetterhead = getProfileLetterhead(posProfile);
    const { getMenuItem, addonsItems, takeawayTable } =
        useContext(PosContext);
    const navigate = useNavigate();

    const { data: allTables } = useGetAllTables();
    const printOrderMutation = usePrintOrder();
    const cancelInvoiceMutation = useCancelInvoice();

    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [printingOrder, setPrintingOrder] = useState(null);
    const [mode, setMode] = useState("today"); // today | all
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelDuplicate, setCancelDuplicate] = useState(false);
    const [now, setNow] = useState(() => Date.now());

    const { data: invoiceData } = useLoadDoctype(
        "POS Invoice",
        selectedInvoice
    );

    const { data: tableOrderRows } = useGetDocTypeList({
        doctype: selectedInvoice ? "Table Order" : null,
        filters: selectedInvoice ? [["link_invoice", "=", selectedInvoice]] : null,
        fields: ["name", "table_description", "customer", "customer_name", "total", "amount", "notes", "creation"],
        sortBy: "modified desc",
        limit: 1,
    });

    const tableOrderName = tableOrderRows && tableOrderRows.length ? tableOrderRows[0].name : null;
    const { data: tableOrderData } = useLoadDoctype("Table Order", tableOrderName);
    const tableOrderDoc = tableOrderData?.docs?.[0] || null;
    const { data: draftOrderData } = useLoadDoctype(
        "Draft Table Order",
        tableOrderDoc?.source_draft_order || null
    );
    const draftOrderDoc = draftOrderData?.docs?.[0] || null;
    const getDuplicatedOrderUrl = (orderName) =>
        `/pos/${getTakeawayUrlSegment(posProfile, takeawayTable)}/${orderName}?from_cancel_duplicate=1`;

    const invoiceDoc = invoiceData?.docs?.[0] || null;
    const allowCancelAfterMinutes = Number(
        restaurantSettings?.docs?.[0]?.allow_cancel_after
    );
    const hasCancelWindow =
        Number.isFinite(allowCancelAfterMinutes) && allowCancelAfterMinutes > 0;
    const cancelWindowMs = hasCancelWindow
        ? allowCancelAfterMinutes * 60 * 1000
        : 0;

    const isRecentInvoice = useMemo(() => {
        if (!hasCancelWindow) return false;
        const raw = invoiceDoc?.creation;
        if (!raw) return false;
        const s = String(raw);
        const isoLike = s.includes(" ") ? s.replace(" ", "T") : s;
        const ms = Date.parse(isoLike);
        if (!Number.isFinite(ms)) return false;
        return Date.now() - ms < cancelWindowMs;
    }, [invoiceDoc?.creation, hasCancelWindow, cancelWindowMs]);

    const isRecentOrder = useMemo(() => {
        if (!hasCancelWindow) return false;
        const raw = tableOrderDoc?.creation;
        if (!raw) return false;
        const s = String(raw);
        const isoLike = s.includes(" ") ? s.replace(" ", "T") : s;
        const ms = Date.parse(isoLike);
        if (!Number.isFinite(ms)) return false;
        return Date.now() - ms < cancelWindowMs;
    }, [tableOrderDoc?.creation, hasCancelWindow, cancelWindowMs]);

    // Debounce search to avoid spamming queries
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(t);
    }, [searchText]);

    useEffect(() => {
        setSelectedInvoice(null);
    }, [posProfile?.name]);

    const closedInvoicesQuery = useClosedInvoices({
        mode,
        limit: 60,
        search: debouncedSearch,
        posProfile: posProfile?.name,
        enabled: open,
    });

    const closedInvoices = useMemo(
        () => (closedInvoicesQuery.data?.pages || []).flat(),
        [closedInvoicesQuery.data?.pages]
    );

    const closedInvoiceNames = useMemo(
        () => closedInvoices.map((o) => o.name).filter(Boolean),
        [closedInvoices]
    );

    const { data: linkedOrderRows } = useGetDocTypeList({
        doctype: open && closedInvoiceNames.length ? "Table Order" : null,
        filters:
            open && closedInvoiceNames.length
                ? [["link_invoice", "in", closedInvoiceNames]]
                : null,
        fields: [
            "name",
            "link_invoice",
            "delivery_provider",
            "designated_number",
            "estimated_preparation_time",
            "creation",
        ],
        sortBy: "creation desc",
        limit: 0,
    });

    const linkedOrderByInvoice = useMemo(() => {
        const map = new Map();
        (linkedOrderRows || []).forEach((row) => {
            if (row?.link_invoice) {
                map.set(row.link_invoice, row);
            }
        });
        return map;
    }, [linkedOrderRows]);

    // Derive list rows without storing derived state (prevents render loops from unstable deps like `.flat()`).
    const orders = useMemo(() => {
        if (!closedInvoices || (!isRetailPos && !allTables)) return [];

        return closedInvoices.map((i) => {
            const _invoice = { ...i };
            if (isRetailPos) {
                return {
                    ..._invoice,
                    customerDisplay:
                        String(
                            _invoice.customer_name || _invoice.customer || ""
                        ).trim() || "-",
                };
            }
            const linkedOrder = linkedOrderByInvoice.get(_invoice.name) || {};
            const invoiceTable = String(_invoice.table || "").trim();
            const designatedNumber = String(
                linkedOrder.designated_number || _invoice.designated_number || ""
            ).trim();
            const isDelivery = !!String(
                linkedOrder.delivery_provider || _invoice.delivery_provider || ""
            ).trim();
            const suffix = _invoice.customer_name?.split("-")?.slice(-1)?.[0];
            const _table =
                allTables.find((t) => t.name === suffix) || {
                    description: t("closedOrders.takeaway"),
                };
            const legacyTable = _table.description;
            const tableDisplay = isDelivery
                ? t("closedOrders.delivery", { defaultValue: "delivery" })
                : designatedNumber || invoiceTable || legacyTable;
            const lowerTableDisplay = String(tableDisplay || "").toLowerCase();
            const orderType = isDelivery
                ? "delivery"
                : designatedNumber || invoiceTable
                ? "dine_in"
                : lowerTableDisplay ===
                  String(t("closedOrders.takeaway")).toLowerCase()
                ? "takeaway"
                : "dine_in";
            return {
                ..._invoice,
                table: invoiceTable || legacyTable,
                tableDisplay,
                orderType,
                customerDisplay: getVisibleCustomerName(
                    _invoice.customer_name,
                    _invoice.customer
                ),
            };
        });
    }, [closedInvoices, allTables, isRetailPos, linkedOrderByInvoice, t]);

    // Only Tabs POS shows the preparation countdown, and only on "Today".
    const showTimers = !isRetailPos && isTabsPos && mode === "today";

    const prepByInvoice = useMemo(() => {
        const map = new Map();
        (linkedOrderRows || []).forEach((row) => {
            if (!row?.link_invoice) return;
            map.set(row.link_invoice, {
                prep: Number(row.estimated_preparation_time) || 0,
                creation: row.creation,
            });
        });
        return map;
    }, [linkedOrderRows]);

    // Tick every second so the countdown stays live (only while relevant).
    useEffect(() => {
        if (!open || !showTimers) return undefined;
        setNow(Date.now());
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [open, showTimers]);

    const parseFrappeTime = (raw) => {
        if (!raw) return NaN;
        const s = String(raw);
        const isoLike = s.includes(" ") ? s.replace(" ", "T") : s;
        return Date.parse(isoLike);
    };

    const renderPrepTimer = (invoiceName) => {
        const info = prepByInvoice.get(invoiceName);
        if (!info || !info.prep) return null;
        const baseMs = parseFrappeTime(info.creation);
        if (!Number.isFinite(baseMs)) return null;

        const targetMs = baseMs + info.prep * 60 * 1000;
        const diffMs = targetMs - now;
        const overdue = diffMs < 0;
        const totalSec = Math.floor(Math.abs(diffMs) / 1000);
        const mm = Math.floor(totalSec / 60);
        const ss = totalSec % 60;
        const label = `${overdue ? "-" : ""}${String(mm).padStart(
            2,
            "0"
        )}:${String(ss).padStart(2, "0")}`;

        return (
            <span
                title={overdue ? t("closedOrders.overdue") : undefined}
                style={{
                    fontWeight: 800,
                    color: overdue ? "#e14954" : "#2e7d32",
                }}
            >
                {label}
            </span>
        );
    };

    const handlePrint = (order) => {
        if (!invoicePrinter) {
            setPrintingOrder(order.name);

            const printUrl = buildDownloadPdfUrl({
                doctype: "POS Invoice",
                name: order.name,
                format: invoicePrintFormat,
                noLetterhead: 0,
                letterhead: selectedLetterhead,
            });

            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = printUrl;
            document.body.appendChild(iframe);

            iframe.onload = () => {
                try {
                    iframe.contentWindow.print();
                } catch (error) {
                    console.error("Error printing invoice:", error);
                }
                setPrintingOrder(null);
            };
        } else {
            setPrintingOrder(order.name);
            printOrderMutation.mutate(
                {
                    orderId: order.name,
                    doctype: "POS Invoice",
                    printer: invoicePrinter,
                    printFormat: invoicePrintFormat,
                },
                {
                    onSuccess: () => {
                        setPrintingOrder(null);
                    },
                    onError: (err) => {
                        console.error("print_by_server failed", err);
                        setPrintingOrder(null);
                    },
                }
            );
        }
    };

    const invoiceDerived = useMemo(() => {
        // Prefer Table Order (it has parent_item + identifier semantics we need)
        const isLinkingToTableOrder = !!selectedInvoice;
        const sourceDoc = tableOrderDoc || null;

        // If we have a selected invoice but Table Order isn't loaded yet, avoid falling back
        // to POS Invoice custom_order_items (which can make children appear as top-level).
        if (isLinkingToTableOrder && !sourceDoc) {
            return {
                parentEntries: [],
                total: 0,
                table: "",
                notes: "",
                createdBy: "",
                submittedBy: "",
                customer: "",
                customerName: "",
                customerMobile: "",
                customerAddress: "",
                deliveryProvider: "",
                totalQty: 0,
                timeLabel: "",
                isLoadingItems: true,
            };
        }

        if (!sourceDoc) {
            return {
                parentEntries: [],
                total: 0,
                table: "",
                notes: "",
                createdBy: "",
                submittedBy: "",
                customer: "",
                customerName: "",
                customerMobile: "",
                customerAddress: "",
                deliveryProvider: "",
                totalQty: 0,
                timeLabel: "",
                isLoadingItems: false,
            };
        }

        const items = sourceDoc.entry_items || [];
        let total = 0;
        items.forEach((it) => {
            total += (it.qty || 0) * (it.rate || 0);
        });

        // Build parent/addon grouping (Model A) using parent_item + identifier, if present.
        const normalizeNull = (v) => {
            if (v === null || v === undefined) return "";
            let s = String(v).trim();
            if (!s) return "";
            // Strip wrapping quotes (e.g. "\"NULL\"")
            if (
                (s.startsWith('"') && s.endsWith('"')) ||
                (s.startsWith("'") && s.endsWith("'"))
            ) {
                s = s.slice(1, -1).trim();
            }
            const lower = s.toLowerCase();
            if (lower === "null" || lower === "none") return "";
            return s;
        };

        const withIds = items.map((row, idx) => {
            const identifier =
                normalizeNull(row.identifier) ||
                normalizeNull(row.name) ||
                `${row.item_code}-${idx}`;
            const parent_item = normalizeNull(row.parent_item);
            return { ...row, __identifier: identifier, __parent_item: parent_item };
        });

        const childrenByParent = new Map();
        const parents = [];

        withIds.forEach((row) => {
            if (row.__parent_item) {
                const list = childrenByParent.get(row.__parent_item) || [];
                list.push(row);
                childrenByParent.set(row.__parent_item, list);
            } else {
                parents.push(row);
            }
        });

        const buildDisplay = (row) => {
            const menuItem = getMenuItem?.(row.item_code);
            const cover = menuItem?.cover || null;
            const item_name = row.item_name || menuItem?.item_name || row.item_code;
            const notes = row.notes || row.custom_notes || row.item_notes || "";
            return {
                id: row.__identifier,
                item_code: row.item_code,
                item_name,
                qty: row.qty || 0,
                rate: row.rate || 0,
                amount: (row.qty || 0) * (row.rate || 0),
                cover,
                notes,
            };
        };

        const parentEntries = parents
            .map((p) => {
                const display = buildDisplay(p);
                const childRows = childrenByParent.get(p.__identifier) || [];
                const addonEntriesModelA = childRows.map((c) => buildDisplay(c));

                // Model B fallback: `addons` is JSON list of item_code strings
                let addonEntriesModelB = [];
                if (addonEntriesModelA.length === 0) {
                    // In Table Order, `addons` is often a JSON array of objects (item_code, rate, qty...)
                    const raw = p.addons ? JSON.parse(p.addons) : [];
                    if (Array.isArray(raw) && raw.length > 0) {
                        addonEntriesModelB = raw
                            .map((a, idx) => {
                                const code = a?.item_code || a?.item || a?.name;
                                if (!code) return null;
                                const meta = getMenuItem?.(code) || addonsItems?.find((x) => x.item_code === code);
                                const rate = Number(a?.standard_rate ?? a?.rate ?? meta?.standard_rate ?? 0);
                                const qty = Number(a?.qty ?? 1);
                                return {
                                    id: `addon:${display.id}:${code}:${idx}`,
                                    item_code: code,
                                    item_name: a?.item_name || meta?.item_name || code,
                                    qty,
                                    rate,
                                    amount: qty * rate,
                                    cover: meta?.cover || meta?.image || null,
                                    notes: a?.notes || "",
                                };
                            })
                            .filter(Boolean);
                    }
                }

                return {
                    ...display,
                    addons: addonEntriesModelA.length ? addonEntriesModelA : addonEntriesModelB,
                };
            })
            // Safety: ensure we never render child rows as top-level even if data is weird
            .filter((p) => !!p.item_code);

        const designatedNumber = String(
            sourceDoc.designated_number || ""
        ).trim();
        const table = designatedNumber
            ? designatedNumber
            : sourceDoc.delivery_provider
            ? t("closedOrders.delivery", { defaultValue: "delivery" })
            : sourceDoc.table_description || t("closedOrders.takeaway");

        const notes =
            sourceDoc.notes ||
            sourceDoc.custom_notes ||
            sourceDoc.custom_order_notes ||
            sourceDoc.remarks ||
            "";
        const tableOrderOwner = sourceDoc.owner || invoiceDoc?.owner || "";
        const draftOrderOwner = draftOrderDoc?.owner || "";
        const createdBy = draftOrderOwner || tableOrderOwner;
        const submittedBy =
            draftOrderOwner && tableOrderOwner !== draftOrderOwner
                ? tableOrderOwner
                : "";

        const customerName = sourceDoc.customer_name || "";
        const customer = sourceDoc.customer || "";
        const customerMobile = sourceDoc.mobile_no || "";
        const customerAddress = [sourceDoc.address_line1, sourceDoc.address_line2]
            .map((part) => String(part || "").trim())
            .filter(Boolean)
            .join(", ");
        const deliveryProvider = sourceDoc.delivery_provider || "";
        const totalQty = sourceDoc.total_qty || 0;
        const timeLabel = sourceDoc.creation ? dateTimeFormat(sourceDoc.creation) : "";

        return {
            parentEntries,
            total,
            table,
            notes,
            createdBy,
            submittedBy,
            customer,
            customerName,
            customerMobile,
            customerAddress,
            deliveryProvider,
            totalQty,
            designatedNumber,
            timeLabel,
            isLoadingItems: false,
        };
    }, [
        selectedInvoice,
        tableOrderDoc,
        draftOrderDoc?.owner,
        invoiceDoc?.owner,
        getMenuItem,
        addonsItems,
        t,
    ]);

    const startCancelFlow = (duplicate) => {
        if (!selectedInvoice) return;
        if (!hasCancelWindow) return;
        if (!isRecentInvoice) return;
        setCancelDuplicate(!!duplicate);
        // If the order is very recent, allow quick cancel+duplicate without the elevated dialog.
        if (duplicate && isRecentOrder && !elevatedPrivileges) {
            cancelInvoiceMutation.mutate(
                { invoice: selectedInvoice, duplicate_order: true },
                {
                    onSuccess: (res) => {
                        const newOrderName = typeof res === "string" ? res : null;
                        setSelectedInvoice(null);
                        closedInvoicesQuery.refetch?.();
                        if (newOrderName) {
                            onClose?.();
                            navigate(getDuplicatedOrderUrl(newOrderName));
                        }
                    },
                }
            );
            return;
        }
        setCancelDialogOpen(true);
    };

    const handleCancelConfirm = async (passwordOrWaste, _qty, _note) => {
        // ConfirmationDialog only provides note/password when elevated; for non-elevated we ignore.
        const duplicate_order = !!cancelDuplicate;
        cancelInvoiceMutation.mutate(
            { invoice: selectedInvoice, duplicate_order },
            {
                onSuccess: (res) => {
                    const newOrderName = typeof res === "string" ? res : null;
                    setCancelDialogOpen(false);
                    setSelectedInvoice(null);
                    closedInvoicesQuery.refetch?.();
                    if (duplicate_order && newOrderName) {
                        onClose?.();
                        navigate(getDuplicatedOrderUrl(newOrderName));
                    }
                },
                onError: (err) => {
                    console.error("cancel_invoice failed", err);
                },
            }
        );
    };

    return (
        <React.Fragment>
            <Drawer
                className={classes.Drawer}
                anchor="right"
                open={open}
                onClose={onClose}
            >
                <div className={classes.DrawerWrapper}>
                    <SimpleWidgetsHeader
                        onClose={onClose}
                        Icon={Archive}
                        title={
                            isRetailPos
                                ? t("retailPos.salesReport")
                                : t("closedOrders.title")
                        }
                        // action={newTakeawayOrder}
                    />

                    <div style={{ padding: "0 1rem 0.75rem 1rem" }}>
                        <div
                            style={{
                                display: "flex",
                                gap: "0.75rem",
                                alignItems: "center",
                                flexWrap: "wrap",
                            }}
                        >
                            <ToggleButtonGroup
                                size="small"
                                value={mode}
                                exclusive
                                onChange={(_, v) => {
                                    if (!v) return;
                                    setMode(v);
                                    // reset selection + list while switching
                                    setSelectedInvoice(null);
                                }}
                            >
                                <ToggleButton value="today">
                                    {t("closedOrders.today")}
                                </ToggleButton>
                                <ToggleButton value="all">
                                    {t("common.all")}
                                </ToggleButton>
                            </ToggleButtonGroup>

                            <TextField
                                size="small"
                                placeholder={
                                    isRetailPos
                                        ? t("retailPos.searchSales")
                                        : t("closedOrders.searchOrders")
                                }
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ flex: 1, minWidth: 180 }}
                            />
                        </div>
                    </div>

                    <div className={classes.OrderItems}>
                        <div className="Orders__List">
                            <div className="Orders__List_RowWrapper">
                                <div className="Orders__List_header">
                                    {!isRetailPos && (
                                        <div>{t("closedOrders.table")}</div>
                                    )}
                                    {(isTabsPos || isRetailPos) ? (
                                        <div>{t("closedOrders.customer")}</div>
                                    ) : null}
                                    <div>
                                        {isRetailPos
                                            ? t("retailPos.invoice")
                                            : t("closedOrders.order")}
                                    </div>
                                    <div>{t("closedOrders.status")}</div>
                                    <div>{t("closedOrders.total")}</div>
                                    <div>{t("closedOrders.time")}</div>
                                    <div>{t("closedOrders.totalItems")}</div>
                                    {showTimers ? (
                                        <div>{t("closedOrders.timer")}</div>
                                    ) : null}
                                </div>
                                <span className="Orders__List_Action">
                                    <IconButton
                                        style={{ visibility: "hidden" }}
                                    >
                                        <Print />
                                    </IconButton>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className={classes.OrderItems}>
                        <div className="Orders__List  List">
                            {closedInvoicesQuery.isLoading ? (
                                <div style={{ padding: "1rem" }}>
                                    <Spinner />
                                </div>
                            ) : null}
                            {orders.map((order, idx) => {
                                return (
                                    <div
                                        key={`order-${idx}`}
                                        className="Orders__List_RowWrapper"
                                    >
                                        <div
                                            className="Orders__List_item"
                                            onClick={() =>
                                                setSelectedInvoice(order.name)
                                            }
                                        >
                                            {!isRetailPos && (
                                                <div>
                                                    <span
                                                        style={orderTypeBadgeStyle(
                                                            order.orderType
                                                        )}
                                                    >
                                                        {order.tableDisplay ||
                                                            order.table}
                                                    </span>
                                                </div>
                                            )}
                                            {(isTabsPos || isRetailPos) ? (
                                                <div>{order.customerDisplay}</div>
                                            ) : null}
                                            <div>
                                                {isRetailPos
                                                    ? order.name
                                                    : getOrderShortName(
                                                          order.name
                                                      )}
                                            </div>
                                            <div>{order.status || "-"}</div>
                                            <div>{order.total}</div>
                                            <div>
                                                {dateTimeFormat(
                                                    order.posting_time
                                                )}
                                            </div>
                                            <div>{order.total_qty}</div>
                                            {showTimers ? (
                                                <div>
                                                    {renderPrepTimer(
                                                        order.name
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                        <span className="Orders__List_Action">
                                            <IconButton
                                                onClick={() =>
                                                    handlePrint(order)
                                                }
                                                disabled={printingOrder === order.name}
                                            >
                                                {printingOrder === order.name ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <Print />
                                                )}
                                            </IconButton>
                                        </span>
                                    </div>
                                );
                            })}

                            {mode === "all" ? (
                                <div style={{ padding: "0.75rem 1rem" }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        disabled={
                                            closedInvoicesQuery.isFetchingNextPage ||
                                            !closedInvoicesQuery.hasNextPage
                                        }
                                        onClick={() =>
                                            closedInvoicesQuery.fetchNextPage()
                                        }
                                    >
                                        {closedInvoicesQuery.isFetchingNextPage
                                            ? t("common.loading")
                                            : closedInvoicesQuery.hasNextPage
                                            ? t("closedOrders.loadMore")
                                            : t("closedOrders.noMore")}
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </Drawer>

            {/* Details drawer (keeps list drawer open) */}
            <Drawer
                anchor="right"
                open={!!selectedInvoice}
                onClose={() => setSelectedInvoice(null)}
                PaperProps={{
                    style: {
                        width: 520,
                        maxWidth: "100%",
                    },
                }}
            >
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <div
                        style={{
                            padding: "0.75rem 1rem",
                            borderBottom: "1px solid #e5e9f2",
                            background: "#f5f6fa",
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.75rem",
                        }}
                    >
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900 }}>
                                {selectedInvoice
                                    ? getOrderShortName(selectedInvoice)
                                    : t("closedOrders.order")}
                            </div>
                            <div style={{ color: "#6E7187", fontSize: 12 }}>
                                {invoiceDerived.timeLabel}
                            </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                            <IconButton
                                onClick={() => {
                                    if (invoiceDoc?.name) handlePrint(invoiceDoc);
                                }}
                                disabled={!invoiceData || printingOrder === selectedInvoice}
                            >
                                {printingOrder === selectedInvoice ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Print />
                                )}
                            </IconButton>
                            <Button
                                color="error"
                                variant="outlined"
                                onClick={() => startCancelFlow(false)}
                                disabled={
                                    !selectedInvoice ||
                                    !isRecentInvoice ||
                                    cancelInvoiceMutation.isLoading
                                }
                            >
                                {t("closedOrders.cancel")}
                            </Button>
                            {!isRetailPos && (
                                <Button
                                    color="error"
                                    variant="contained"
                                    onClick={() => startCancelFlow(true)}
                                    disabled={
                                        !selectedInvoice ||
                                        !isRecentInvoice ||
                                        cancelInvoiceMutation.isLoading
                                    }
                                >
                                    {t("closedOrders.cancelAndDuplicate")}
                                </Button>
                            )}
                            <Button onClick={() => setSelectedInvoice(null)}>Close</Button>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflow: "auto", padding: "1rem" }}>
                        {!!invoiceDoc ? (
                            <InvoiceDetails
                                classes={classes}
                                parentEntries={invoiceDerived.parentEntries}
                                total={invoiceDerived.total}
                                invoiceDoc={invoiceDoc}
                                isRetailPos={isRetailPos}
                                summary={{
                                    table: invoiceDerived.table,
                                    createdBy: invoiceDerived.createdBy,
                                    submittedBy: invoiceDerived.submittedBy,
                                    customerName:
                                        invoiceDerived.customerName ||
                                        invoiceDoc?.customer_name,
                                    customer:
                                        invoiceDerived.customer ||
                                        invoiceDoc?.customer,
                                    customerMobile:
                                        invoiceDerived.customerMobile,
                                    customerAddress:
                                        invoiceDerived.customerAddress,
                                    deliveryProvider:
                                        invoiceDerived.deliveryProvider,
                                    totalQty: invoiceDerived.totalQty,
                                    designatedNumber:
                                        invoiceDerived.designatedNumber,
                                    notes: invoiceDerived.notes,
                                    isLoadingItems: invoiceDerived.isLoadingItems,
                                }}
                            />
                        ) : (
                            <Spinner />
                        )}
                    </div>
                </div>
            </Drawer>

            <ConfirmationDialog
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                title={
                    cancelDuplicate
                        ? t("closedOrders.cancelAndDuplicate")
                        : t("closedOrders.cancel")
                }
                message={
                    cancelDuplicate
                        ? t("closedOrders.cancelAndDuplicateConfirm")
                        : t("closedOrders.cancelConfirm")
                }
                confirmText={t("common.confirm")}
                confirmColor="error"
                cancelText={t("common.cancel")}
                loading={cancelInvoiceMutation.isLoading}
                item={{ elevatedPrivileges: !!elevatedPrivileges }}
                onConfirm={handleCancelConfirm}
            />
        </React.Fragment>
    );
}

function SummaryRow({ label, value }) {
    if (!value) return null;
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                padding: "0.35rem 0",
                borderBottom: "1px dashed #e5e9f2",
            }}
        >
            <div style={{ color: "#6E7187", fontWeight: 800, fontSize: 12 }}>
                {label}
            </div>
            <div style={{ fontWeight: 800, fontSize: 13, textAlign: "right" }}>
                {value}
            </div>
        </div>
    );
}

function InvoiceDetails({
    classes,
    invoiceDoc,
    isRetailPos,
    parentEntries,
    total,
    summary,
}) {
    const { t } = useTranslation();
    const retailTotal =
        Number(invoiceDoc?.grand_total ?? invoiceDoc?.rounded_total ?? total) ||
        0;
    const paymentSummary = (invoiceDoc?.payments || [])
        .filter((payment) => payment?.mode_of_payment)
        .map((payment) => {
            const amount = Number(payment.amount || 0).toFixed(2);
            return `${payment.mode_of_payment}: ${amount}`;
        })
        .join(", ");

    const [itemSearch, setItemSearch] = useState("");
    const filteredParentEntries = useMemo(() => {
        const q = String(itemSearch || "").trim().toLowerCase();
        if (!q) return Array.isArray(parentEntries) ? parentEntries : [];
        const list = Array.isArray(parentEntries) ? parentEntries : [];

        const matchesEntry = (e) => {
            if (!e) return false;
            const hay = [
                e.item_name,
                e.item_code,
                e.notes,
                ...(Array.isArray(e.addons)
                    ? e.addons.flatMap((a) => [a?.item_name, a?.item_code, a?.notes])
                    : []),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(q);
        };

        return list.filter(matchesEntry);
    }, [parentEntries, itemSearch]);

    return (
        <div>
            {/* Summary */}
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #e5e9f2",
                    borderRadius: 12,
                    padding: "0.75rem",
                    marginBottom: "0.75rem",
                }}
            >
                {isRetailPos ? (
                    <SummaryRow
                        label={t("retailPos.invoice")}
                        value={invoiceDoc?.name}
                    />
                ) : (
                    <>
                        <SummaryRow
                            label={t("closedOrders.table")}
                            value={summary?.table}
                        />
                        <SummaryRow
                            label={t("closedOrders.createdBy")}
                            value={summary?.createdBy}
                        />
                        <SummaryRow
                            label={t("closedOrders.submittedBy")}
                            value={summary?.submittedBy}
                        />
                    </>
                )}
                {/* <SummaryRow
                    label={t("closedOrders.designatedNumber")}
                    value={summary?.designatedNumber}
                /> */}
                <SummaryRow
                    label={t("closedOrders.customer")}
                    value={summary?.customerName || summary?.customer}
                />
                <SummaryRow
                    label={t("closedOrders.customerMobile")}
                    value={summary?.customerMobile}
                />
                {!isRetailPos && (
                    <>
                        <SummaryRow
                            label={t("closedOrders.customerAddress")}
                            value={summary?.customerAddress}
                        />
                        <SummaryRow
                            label={t("closedOrders.deliveryProvider")}
                            value={summary?.deliveryProvider}
                        />
                    </>
                )}
                {isRetailPos && (
                    <SummaryRow
                        label={t("retailPos.payment")}
                        value={paymentSummary}
                    />
                )}
                <SummaryRow
                    label={t("closedOrders.totalItems")}
                    value={summary?.totalQty ? String(summary.totalQty) : ""}
                />
                <SummaryRow
                    label={t("closedOrders.total")}
                    value={
                        isRetailPos
                            ? retailTotal.toFixed(2)
                            : Number.isFinite(total)
                            ? String(total)
                            : ""
                    }
                />
                {summary?.notes ? (
                    <div style={{ paddingTop: "0.5rem" }}>
                        <div
                            style={{
                                color: "#6E7187",
                                fontWeight: 900,
                                fontSize: 12,
                                marginBottom: "0.25rem",
                            }}
                        >
                            {t("bundle.notes")}
                        </div>
                        <div
                            style={{
                                background: "rgba(251, 253, 245, 0.75)",
                                border: "1px solid #e5e9f2",
                                borderRadius: 10,
                                padding: "0.6rem",
                                fontWeight: 700,
                                whiteSpace: "pre-wrap",
                            }}
                        >
                            {summary.notes}
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Items */}
            <div className={classes.OrderWidget}>
                {Array.isArray(parentEntries) && parentEntries.length > 5 ? (
                    <div style={{ padding: "0 0.5rem 0.5rem 0.5rem" }}>
                        <TextField
                            size="small"
                            placeholder={t("closedOrders.searchItems")}
                            value={itemSearch}
                            onChange={(e) => setItemSearch(e.target.value)}
                            fullWidth
                        />
                    </div>
                ) : null}
                <div className={classes.OrderCartItems}>
                    {summary?.isLoadingItems ? (
                        <div style={{ padding: "0.5rem", color: "#6E7187" }}>
                            {t("closedOrders.loadingItems")}
                        </div>
                    ) : null}
                    {Array.isArray(filteredParentEntries) &&
                    filteredParentEntries.length > 0 ? (
                        filteredParentEntries.map((entry) => (
                            <ClosedOrderItemRow key={entry.id} entry={entry} />
                        ))
                    ) : (
                        !summary?.isLoadingItems ? (
                            <div style={{ padding: "0.5rem", color: "#6E7187" }}>
                                {itemSearch
                                    ? t("closedOrders.noMatchingItems")
                                    : t("closedOrders.noItems")}
                            </div>
                        ) : null
                    )}
                </div>
            </div>
        </div>
    );
}
export default ClosedOrders;
